/*
Draft state helpers for the email template editor (Task 6, phase 3 of the
email campaigns PR series).

draftFromTemplate turns a server EmailTemplate (or nothing, for a brand-new
template) into the flat, form-shaped draft the editor screen (Task 7) binds
its inputs to. isDirty compares a draft against its original for the "unsaved
changes" indicator. validateDraft mirrors the server's save-blocking checks
so the editor can warn *before* a save the server would reject, rather than
after a round trip.

validateDraft mirrors two files on the backend:

1. ps-back/util/email/extractVariables.js - the Handlebars-variable scanner.
   The regex below, `/\{\{\{?([^{}]+)\}?\}\}/g`, and the NOT_A_VARIABLE sigil
   list are a byte-for-byte port of that file. A divergent regex here would
   mean the client and server disagree about what a template's variables
   are - see that file's own comment for why the naive `/\{\{([^}]+)\}\}/g`
   is wrong (it eats the first two braces of a triple-stache).

2. ps-back/util/email/validateTemplateContent.js - the two save-blocking
   checks from spec decision 16a: BLANK_SUBJECT and UNPROVIDED_VARIABLE.
   validateDraft also folds preheader into the scanned text exactly the way
   ps-back/graphql/resolvers/mutations/admin/editEmailTemplate.js does before
   calling validateTemplateContent (`textContent: `${preheader} ${textContent}``) -
   otherwise a variable used only in the preheader would validate here but
   still get rejected by the server. A third, additional check -
   INVALID_SAMPLE_DATA_JSON - mirrors that same resolver's defensive
   JSON.parse(sampleDataJson), which also blocks a save on bad JSON.

CommonJS on purpose: no bundler, runs under plain node
src/utils/emailDraft.test.js (same pattern as src/utils/emailPlates.js).
*/

// --- Ported from ps-back/util/email/extractVariables.js ---
const TOKEN = /\{\{\{?([^{}]+)\}?\}\}/g;
const NOT_A_VARIABLE = ['#', '/', '^', '!', '>', '&'];

const extractVariables = (content) => {
  if (!content) return [];

  const variables = new Set();
  let match = TOKEN.exec(content);

  while (match !== null) {
    const name = match[1].trim().split(/[\s|]/)[0];
    if (name && !NOT_A_VARIABLE.includes(name[0])) variables.add(name);
    match = TOKEN.exec(content);
  }

  TOKEN.lastIndex = 0;
  return Array.from(variables);
};

const DRAFT_FIELDS = ['subject', 'preheader', 'htmlContent', 'textContent', 'sampleDataJson'];

const draftFromTemplate = (template = {}) => {
  const source = template || {};

  let sampleDataJson = '';
  const { sampleDataJson: sourceSampleDataJson } = source;
  if (typeof sourceSampleDataJson === 'string') {
    // Already a string (e.g. round-tripped through a previous draft) - keep as-is.
    sampleDataJson = sourceSampleDataJson;
  } else if (source.sampleData && typeof source.sampleData === 'object'
    && Object.keys(source.sampleData).length > 0) {
    sampleDataJson = JSON.stringify(source.sampleData, null, 2);
  }

  return {
    subject: source.subject || '',
    preheader: source.preheader || '',
    htmlContent: source.htmlContent || '',
    textContent: source.textContent || '',
    sampleDataJson,
  };
};

const isDirty = (draft = {}, original = {}) => DRAFT_FIELDS.some(
  field => (draft[field] || '') !== (original[field] || ''),
);

const validateDraft = (draft = {}, providedVariables = []) => {
  const {
    subject = '', preheader = '', htmlContent = '', textContent = '', sampleDataJson = '',
  } = draft || {};
  const provided = providedVariables || [];
  const errors = [];

  // Mirrors validateTemplateContent.js's BLANK_SUBJECT check.
  if (!String(subject).trim()) {
    errors.push({ code: 'BLANK_SUBJECT', message: 'The subject cannot be empty.' });
  }

  // Mirrors editEmailTemplate.js's defensive sampleDataJson parse. An empty
  // string is not malformed - it just means no sample data yet.
  if (sampleDataJson && String(sampleDataJson).trim()) {
    try {
      JSON.parse(sampleDataJson);
    } catch (parseError) {
      errors.push({ code: 'INVALID_SAMPLE_DATA_JSON', message: 'sampleDataJson is not valid JSON' });
    }
  }

  // Mirrors validateTemplateContent.js's UNPROVIDED_VARIABLE check, with
  // preheader folded in the same way editEmailTemplate.js folds it into the
  // textContent it passes down: `${subject} ${htmlContent} ${preheader} ${textContent}`.
  const used = extractVariables(`${subject} ${htmlContent} ${preheader} ${textContent}`);
  used.forEach((variable) => {
    if (!provided.includes(variable)) {
      errors.push({
        code: 'UNPROVIDED_VARIABLE',
        variable,
        message: `The subject or body uses {{${variable}}}, which nothing provides for this campaign.`,
      });
    }
  });

  return { valid: errors.length === 0, errors };
};

module.exports = {
  extractVariables,
  draftFromTemplate,
  isDirty,
  validateDraft,
};
